"use client";
import type { ReactNode } from 'react';

import Link from 'next/link';
import { Component } from 'react';

import { ERROR_MESSAGES, ROUTES } from '../constants';

interface ErrorBoundaryState {
  error: Error | null;
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
          <div className="explanation-box-semantic bg-semantic-error/15 border border-semantic-error/30 backdrop-blur-sm max-w-md mx-4">
            <h2 className="text-semantic-error text-2xl font-bold mb-4">
              Something went wrong
            </h2>
            <p className="text-secondary mb-4">
              {this.state.error?.message || ERROR_MESSAGES.UNKNOWN_ERROR}
            </p>
            <div className="flex gap-4">
              <button
                aria-label="Reload the current page to try again"
                className="bg-semantic-error/20 hover:bg-semantic-error/30 text-semantic-error px-4 py-2 rounded-lg transition-colors"
                onClick={() => window.location.reload()}
                type="button"
              >
                Reload Page
              </button>
              <Link
                className="bg-brand-primary/20 hover:bg-brand-primary/30 text-brand-primary px-4 py-2 rounded-lg transition-colors"
                href={ROUTES.CONCEPT_LIST}
              >
                Go Home
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
